<template>
  <div class="shadow border rounded-lg">
    <!-- Header -->
    <div class="p-4 border-b bg-light-grey rounded-t-lg">
      <div class="flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-vice-teal-700">Shared With</h3>
        <button
          @click="toggleAddUsersModal(true)"
          type="button"
          class="
            inline-flex
            items-center
            px-3
            py-1.5
            border border-transparent
            rounded-md
            shadow-sm
            text-sm
            font-medium
            text-white
            bg-vice-teal-600
            hover:bg-vice-teal-700
            focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
          "
        >
          <PlusIcon class="h-4 w-4 mr-1" />
          Add Users
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="px-3 py-3 bg-white rounded-b-lg">
      <div v-if="sharedWith.length === 0" class="text-center py-8 text-sm text-gray-500">
        This {{ shareableType }} is not shared with anyone yet.
      </div>

      <div v-else class="space-y-2 max-h-96 overflow-scroll">
        <ShareableUserRow
          v-for="user in sharedWith"
          :key="user.id"
          :user="user"
          :removing="removingUserId === user.id"
          @remove="removeUser"
        />
      </div>
    </div>

    <!-- Add Users Modal -->
    <AddUsersModal
      v-if="showAddUsersModal"
      :sessionId="sessionId"
      :show="showAddUsersModal"
      :shareableId="shareableId"
      :shareableType="shareableType"
      :alreadySharedUserIds="sharedUserIds"
      @usersAdded="onUsersAdded"
      @close="toggleAddUsersModal(false)"
    />
    <ActionMessage :on="showToast" message="Your changes have been saved" />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { PlusIcon } from '@heroicons/vue/solid';
import AddUsersModal from './AddUsersModal.vue';
import ShareableUserRow from './ShareableUserRow.vue';
import ActionMessage from '@/Jetstream/ActionMessage.vue';

export default {
  name: 'ShareableManager',
  emits: ['onShareableUpdate'],
  components: {
    PlusIcon,
    ActionMessage,
    AddUsersModal,
    ShareableUserRow,
  },
  props: {
    shareableType: {
      type: String,
      required: true,
      validator: (value) => ['recording', 'snapshot'].includes(value),
    },
    shareableId: {
      type: Number,
      required: true,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    sharedWith: {
      type: Array,
    },
  },
  setup(props, { emit }) {
    const showToast = ref(false);
    const removingUserId = ref(null);
    const showAddUsersModal = ref(false);

    const sharedUserIds = computed(() => props.sharedWith.map((u) => u.id));

    watch(showToast, (newVal) => {
      if (newVal) {
        setTimeout(() => {
          showToast.value = false;
        }, 100);
      }
    });

    const removeUser = async (userId) => {
      if (removingUserId.value) return;

      removingUserId.value = userId;

      try {
        const response = await axios.delete(
          route('shareables.destroy', {
            type: props.shareableType,
            id: props.shareableId,
            userId,
          }),
        );

        if (response.data.success) {
          emit(
            'onShareableUpdate',
            props.sharedWith.filter((u) => u.id !== userId),
          );
          showToast.value = true;
        } else {
          console.log('Failed to revoke access. Please try again.');
        }
      } catch (error) {
        console.error('Error removing user:', error);
      } finally {
        removingUserId.value = null;
      }
    };

    const toggleAddUsersModal = (show = false) => {
      showAddUsersModal.value = show;
    };

    const onUsersAdded = (newSharedUsers) => {
      emit('onShareableUpdate', newSharedUsers);
      showToast.value = true;

      toggleAddUsersModal(false);
    };

    return {
      showToast,
      sharedUserIds,
      removingUserId,
      showAddUsersModal,
      removeUser,
      onUsersAdded,
      toggleAddUsersModal,
    };
  },
};
</script>
